import { IconButton } from "@/design-system/IconButton/IconButton";
import { NewTag } from "@/design-system/Tags/NewTag/NewTag";
import { OnboardingProgressBadge } from "@/features/MyClub/TryNext/onboardingProgress";
import { WingsCupBanner, WingsCupMenuLink } from "@/features/WingsCup/WingsCup";
import {
  useVisitorCountry,
  useWingsCup,
} from "@/features/WingsCup/useWingsCup";
import { useEscape } from "@/hooks/useKeyboardShortcut";
import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import { AppPromoNavCard } from "@/pages/AppLanding/AppPromoNavCard";
import { Subscription } from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { ChevronDown, Menu, X } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import { AccountCluster } from "./AccountCluster";
import { NavDirectoryGroup, NavPageTile } from "./NavPageTile";
import {
  SITE_MAP_COLUMNS,
  findActiveNav,
  useSiteNavSections,
} from "./siteNavConfig";
import { useSectionHover } from "./useSectionHover";

/*
  Translated labels can make the bar wider than the viewport just above the
  touch breakpoint. Nothing in the bar may shrink, so instead the
  `data-squeeze` pieces are hidden in order of their number (1 = language
  code, 2 = profile label, 3 = credits suffix) until the bar fits. The resize
  observer defers this a frame: mutating inside the callback trips the
  ResizeObserver loop error.
*/
const fitBar = (bar: HTMLDivElement | null) => {
  if (!bar) return;
  const pieces = [...bar.querySelectorAll<HTMLElement>("[data-squeeze]")].sort(
    (a, b) => Number(a.dataset.squeeze) - Number(b.dataset.squeeze),
  );
  pieces.forEach((piece) => (piece.hidden = false));
  for (const piece of pieces) {
    if (bar.scrollWidth <= bar.clientWidth) break;
    piece.hidden = true;
  }
};

// Wings Cup campaign: its Tournaments group closes this site map column.
const TOURNAMENTS_COLUMN = 2;

/** Option 3a: top bar, a mega panel per section on hover, Menu opens the site map. */
export const NavBarMega = ({
  userIsLoggedIn,
  userSubscription,
  paidUserButNotUltimate,
}: {
  userIsLoggedIn: boolean;
  userSubscription: Subscription;
  paidUserButNotUltimate: boolean;
}) => {
  const { t } = useTranslation(["nav", "common"]);
  const { pathname } = useLocation();
  const sections = useSiteNavSections();
  const active = findActiveNav(sections, pathname);
  const hover = useSectionHover();
  const barRef = useRef<HTMLDivElement>(null);

  // Before paint on every render, and a frame after any resize (see fitBar).
  useLayoutEffect(() => fitBar(barRef.current));
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => fitBar(bar));
    });
    observer.observe(bar);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);
  const [siteMapOpen, setSiteMapOpen] = useState(false);
  const wingsCup = useWingsCup();
  const countryKnown = !!useVisitorCountry();

  const closeAll = () => {
    hover.close();
    setSiteMapOpen(false);
  };

  useEffect(() => closeAll(), [pathname]);
  useEscape(closeAll, !!hover.hovered || siteMapOpen);
  useScrollLock(siteMapOpen);

  // Opens over the site map too, which stays open underneath.
  const panelOpen = !!hover.hovered;
  const panelSection = sections.find((s) => s.key === hover.last);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRefs = useRef(new Map<string, HTMLElement>());
  const [panelLeft, setPanelLeft] = useState(0);
  // Anchors the dropdown under its trigger.
  useLayoutEffect(() => {
    const trigger = hover.last && triggerRefs.current.get(hover.last);
    if (!trigger || !rootRef.current) return;
    setPanelLeft(
      trigger.getBoundingClientRect().left -
        rootRef.current.getBoundingClientRect().left,
    );
  }, [hover.last]);

  return (
    <div ref={rootRef} className="relative" onMouseLeave={hover.scheduleClose}>
      <nav className="relative z-10 h-12 border-b border-edge bg-background px-4 large-desktop:px-8">
        <div
          ref={barRef}
          className="mx-auto flex h-full w-full max-w-content items-center gap-x-3"
        >
          <IconButton
            icon={
              siteMapOpen ? (
                <X className="size-4 text-foreground" />
              ) : (
                <Menu className="size-4 text-foreground" />
              )
            }
            aria-label={siteMapOpen ? t("siteNav.close") : t("siteNav.menu")}
            aria-expanded={siteMapOpen}
            onClick={() => {
              hover.close();
              setSiteMapOpen((open) => !open);
            }}
            onMouseEnter={hover.scheduleClose}
          />
          <Link to="/" className="shrink-0" onMouseEnter={hover.scheduleClose}>
            <img
              src={import.meta.env.VITE_APP_ASSETS_BASE_URL + "/easysbc1.png"}
              alt={t("common:brand")}
              className="hidden h-7 large-desktop:block"
            />
            {/* Below large-desktop the bar is full, so only the mark stays. */}
            <img
              src="/logo.png"
              alt={t("common:brand")}
              className="h-7 large-desktop:hidden"
            />
          </Link>
          <div className="flex h-full items-stretch">
            {sections
              .filter((section) => !section.siteMapOnly)
              .map((section) => {
                const isActive = section.key === active.sectionKey;
                const isOpen = panelOpen && hover.hovered === section.key;
                const barTag = section.pages[0].tag;
                return (
                  <Link
                    key={section.key}
                    ref={(el) => {
                      if (el) triggerRefs.current.set(section.key, el);
                    }}
                    to={section.href}
                    aria-expanded={isOpen}
                    onMouseEnter={() => hover.show(section.key)}
                    onFocus={(e) => {
                      // Keyboard only: returning to the tab refocuses the clicked link.
                      if (e.currentTarget.matches(":focus-visible"))
                        hover.showNow(section.key);
                    }}
                    onClick={(e) => {
                      // No hover on touch: the first tap opens the panel.
                      const touch =
                        (e.nativeEvent as PointerEvent).pointerType === "touch";
                      if (touch && !isOpen) {
                        e.preventDefault();
                        hover.showNow(section.key);
                        return;
                      }
                      closeAll();
                    }}
                    className="group flex h-full items-center px-0.5"
                  >
                    <span
                      className={cn(
                        "flex items-center gap-x-1 rounded px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-colors duration-[120ms]",
                        isActive || isOpen
                          ? "text-foreground"
                          : "text-foreground-muted",
                        isOpen
                          ? "bg-surface"
                          : "group-hover:bg-surface group-hover:text-foreground",
                      )}
                    >
                      {section.label}
                      {barTag && <NewTag tag={t(`common:tags.${barTag}`)} />}
                      {section.key === "club" && <OnboardingProgressBadge />}
                      <ChevronDown
                        className="size-2.5 opacity-60"
                        strokeWidth={3}
                      />
                    </span>
                  </Link>
                );
              })}
          </div>
          <div className="ml-auto" onMouseEnter={hover.scheduleClose}>
            <AccountCluster
              userIsLoggedIn={userIsLoggedIn}
              userSubscription={userSubscription}
              paidUserButNotUltimate={paidUserButNotUltimate}
              showSearch
            />
          </div>
        </div>
      </nav>

      {/* Stays mounted so moving between sections swaps content without a flash;
          inert while hidden so Tab can't land on links nobody can see. */}
      <div
        inert={!panelOpen}
        onMouseEnter={hover.cancelClose}
        style={{ left: panelLeft }}
        className={cn(
          "absolute top-full z-10 flex w-max max-w-sm flex-col gap-y-1 rounded-b-lg border border-t-0 border-edge bg-background p-2 shadow-[0_30px_60px_rgba(0,0,0,.55)] transition-[opacity,translate] duration-[160ms] ease-out",
          panelOpen
            ? "translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-2 opacity-0",
        )}
      >
        {panelSection?.pages
          .filter((page) => !page.siteMapOnly)
          .map((page) => (
            <NavPageTile
              key={page.key}
              page={page}
              isCurrent={page.key === active.pageKey}
              onNavigate={closeAll}
            />
          ))}
      </div>

      {siteMapOpen && (
        <div
          aria-hidden
          onClick={closeAll}
          className="absolute inset-x-0 top-full h-dvh bg-black/60"
        />
      )}
      {siteMapOpen && (
        // Content height like the hover panels; scrolls on short screens.
        <div
          onMouseEnter={hover.scheduleClose}
          className="absolute inset-x-0 top-full max-h-[calc(100dvh-48px)] overflow-y-auto border-b border-edge bg-background px-4 py-7 large-desktop:px-8 shadow-[0_30px_60px_rgba(0,0,0,.55)]"
        >
          <div
            className={cn(
              "mx-auto flex max-w-content",
              // Tighter columns while a card sits beside them.
              wingsCup ? "gap-x-10" : "gap-x-20 large-desktop:gap-x-10",
            )}
          >
            {SITE_MAP_COLUMNS.map((column, index) => {
              // The last column is info, not tools: split off and muted.
              const subtle = index === SITE_MAP_COLUMNS.length - 1;
              return (
                <div
                  key={column.join()}
                  className={cn(
                    "flex flex-col gap-y-8",
                    subtle && "-ml-10 border-l border-edge pl-10",
                    subtle &&
                      (wingsCup
                        ? "-ml-5 pl-5"
                        : "large-desktop:-ml-5 large-desktop:pl-5"),
                  )}
                >
                  {column.map((key) => (
                    <NavDirectoryGroup
                      key={key}
                      section={sections.find((s) => s.key === key)!}
                      onNavigate={closeAll}
                      subtle={subtle}
                    />
                  ))}
                  {index === TOURNAMENTS_COLUMN && (
                    <WingsCupMenuLink variant="desktop" />
                  )}
                </div>
              );
            })}
            {/* Wings Cup partner card while it runs, else the app waitlist.
                Below large-desktop there is no room beside the columns. */}
            <div className="hidden min-w-0 flex-1 self-start large-desktop:block">
              {wingsCup ? (
                <WingsCupBanner unit="navigation" />
              ) : (
                // Until the country is known Wings Cup may still claim the slot.
                countryKnown && <AppPromoNavCard onNavigate={closeAll} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
